import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isHome = location.pathname === '/';
  const navBg =
    isScrolled || !isHome ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent';
  const textColor =
    isScrolled || !isHome ? 'text-gray-800' : 'text-white';

  const navLinks = [
    { name: 'Home', path: '/', icon: 'ri-home-4-line' },
    { name: 'About', path: '/about', icon: 'ri-information-line' },
    { name: 'Products', path: '/products', icon: 'ri-plant-line' },
    { name: 'Gallery', path: '/gallery', icon: 'ri-gallery-line' },
    { name: 'Contact', path: '/contact', icon: 'ri-mail-send-line' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div
        className={`${
          isHome
            ? 'absolute top-0 left-0 right-0 z-40'
            : ''
        } transition-all duration-300 hidden md:block relative overflow-hidden`}
      >
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 bg-[length:200%_100%] animate-gradient-move"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center py-2">
            <div className="flex items-center gap-6 text-sm">
              <a
                href="mailto:MangerSales@al-amein.com"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
              >
                <i className="ri-mail-line"></i>
                <span className="hidden sm:inline">MangerSales@al-amein.com</span>
              </a>
              <a
                href="tel:+201069539609"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
              >
                <i className="ri-phone-line"></i>
                <span>+201069539609</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://web.facebook.com/profile.php?id=100091249341012"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-full relative overflow-hidden group cursor-pointer bg-white/10 backdrop-blur-md border border-white/20"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-facebook-fill text-white relative z-10"></i>
              </a>
              <a
                href="https://www.linkedin.com/company/al-amein-for-import-export-and-agricultural-investement/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-full relative overflow-hidden group cursor-pointer bg-white/10 backdrop-blur-md border border-white/20"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-linkedin-fill text-white relative z-10"></i>
              </a>
              <a
                href="https://wa.me/201069539609"
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 flex items-center justify-center rounded-full relative overflow-hidden group cursor-pointer bg-white/10 backdrop-blur-md border border-white/20"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-whatsapp-line text-white relative z-10"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`${
          isHome ? 'fixed' : 'sticky'
        } top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
        style={{ marginTop: isHome && !isScrolled ? '40px' : '0' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 md:py-4">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 relative z-10">
              <div className={`${isScrolled || !isHome ? 'bg-transparent' : 'bg-white/10 backdrop-blur-md rounded-xl p-2'} transition-all duration-300`}>
                <img
                  src="/logo/logo-en-2.png"
                  alt="Al-Amein Logo"
                  className={`h-10 md:h-12 w-auto transition-all duration-300 ${isScrolled || !isHome ? '' : ''}`}
                />
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-5 py-2 text-sm font-medium ${textColor} hover:text-emerald-500 transition-all duration-300 relative group whitespace-nowrap ${
                    location.pathname === link.path ? 'text-emerald-500' : ''
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-emerald-500 transition-all duration-300 ${
                      location.pathname === link.path ? 'w-6' : 'w-0 group-hover:w-6'
                    }`}
                  ></span>
                </Link>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="relative px-6 py-2.5 text-white text-sm font-semibold rounded-full overflow-hidden whitespace-nowrap group"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient-move"></span>
                <span className="absolute inset-0 bg-white/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <span className="relative z-10">Get Quote</span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-12 h-12 flex items-center justify-center rounded-xl relative overflow-hidden cursor-pointer transition-all duration-300 ${
                isScrolled || !isHome ? 'bg-emerald-50' : 'bg-white/10 backdrop-blur-md'
              }`}
            >
              <span className={`absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient-move opacity-0 ${isMobileMenuOpen ? 'opacity-100' : 'group-hover:opacity-100'} transition-opacity duration-300`}></span>
              <i className={`text-2xl relative z-10 transition-all duration-300 ${isMobileMenuOpen ? 'ri-close-line text-white' : `ri-menu-line ${isScrolled || !isHome ? 'text-emerald-600' : 'text-white'}`}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Full Screen Mobile Menu */}
      <div
        className={`fixed inset-0 z-[100] lg:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 bg-[length:400%_400%] animate-gradient-move"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-40 right-10 w-40 h-40 bg-teal-500/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl"></div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col safe-area-top safe-area-bottom">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4">
            <img
              src="/logo/logo-en-2.png"
              alt="Al-Amein Logo"
              className="h-10 w-auto brightness-0 invert"
            />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 cursor-pointer relative overflow-hidden group"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              <i className="ri-close-line text-2xl text-white relative z-10"></i>
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 flex flex-col justify-center px-8">
            <nav className="space-y-3">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-4 px-6 py-4 rounded-2xl transition-all duration-300 group ${
                    location.pathname === link.path
                      ? 'bg-white/20 backdrop-blur-md'
                      : 'hover:bg-white/10'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl relative overflow-hidden ${
                    location.pathname === link.path ? 'bg-white/20' : 'bg-white/10'
                  }`}>
                    <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className={`${link.icon} text-xl text-white relative z-10`}></i>
                  </div>
                  <span className="text-xl font-semibold text-white">{link.name}</span>
                  {location.pathname === link.path && (
                    <span className="ml-auto w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  )}
                </Link>
              ))}
            </nav>
          </div>

          {/* Bottom Section */}
          <div className="px-8 pb-8">
            {/* CTA Button */}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full py-4 text-center text-white text-lg font-semibold rounded-2xl relative overflow-hidden mb-6"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient-move"></span>
              <span className="absolute inset-0 bg-white/20 backdrop-blur-sm"></span>
              <span className="relative z-10 flex items-center justify-center gap-2">
                Get a Quote
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>

            {/* Social Links */}
            <div className="flex items-center justify-center gap-4">
              <a
                href="https://web.facebook.com/profile.php?id=100091249341012"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 relative overflow-hidden group cursor-pointer"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-facebook-fill text-xl text-white relative z-10"></i>
              </a>
              <a
                href="https://www.linkedin.com/company/al-amein-for-import-export-and-agricultural-investement/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 relative overflow-hidden group cursor-pointer"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-linkedin-fill text-xl text-white relative z-10"></i>
              </a>
              <a
                href="https://wa.me/201069539609"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 relative overflow-hidden group cursor-pointer"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-whatsapp-line text-xl text-white relative z-10"></i>
              </a>
              <a
                href="tel:+201069539609"
                className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 relative overflow-hidden group cursor-pointer"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <i className="ri-phone-fill text-xl text-white relative z-10"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
